import { create } from 'zustand';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import type { CreatorDeliveryTimeline } from '@/interface/creatorDeliveryTimeline';
import {
  isCreatorTimelineAccessError,
  getCreatorTimelineContentNodes
} from '@/biz/multiPlatformCollaboration/creatorDeliveryTimeline';

interface TimelineStore {
  orderId: string;
  timeline: CreatorDeliveryTimeline | null;
  receivedAt: number;
  loading: boolean;
  error: Error | null;
  writesDisabled: boolean;
  selectedNodeId: string | null;
  selectNode: (id: string) => void;
  beginRefresh: (orderId: string) => void;
  load: (orderId: string) => Promise<CreatorDeliveryTimeline | null>;
  disableWrites: () => void;
  reset: () => void;
}
let requestVersion = 0;
export const useCreatorDeliveryTimelineStore = create<TimelineStore>(
  (set, get) => ({
    orderId: '',
    timeline: null,
    receivedAt: 0,
    loading: false,
    error: null,
    writesDisabled: false,
    selectedNodeId: null,
    selectNode: (selectedNodeId) => set({ selectedNodeId }),
    beginRefresh: (orderId) => {
      requestVersion += 1;
      const sameOrder = get().orderId === orderId;
      set({
        orderId,
        loading: true,
        error: null,
        ...(!sameOrder
          ? {
              timeline: null,
              receivedAt: 0,
              writesDisabled: false,
              selectedNodeId: null
            }
          : {})
      });
    },
    load: async (orderId) => {
      const request = ++requestVersion;
      const sameOrder = get().orderId === orderId;
      set({
        orderId,
        loading: true,
        error: null,
        ...(!sameOrder
          ? {
              timeline: null,
              writesDisabled: false,
              receivedAt: 0,
              selectedNodeId: null
            }
          : {})
      });
      try {
        const result = await creatorOrderV2Api.deliveryTimeline(orderId);
        if (request !== requestVersion) return null;
        const selectedNodeId = getCreatorTimelineContentNodes(result.data).some(
          (node) =>
            node.id === get().selectedNodeId &&
            node.actions.length > 0 &&
            node.data.target
        )
          ? get().selectedNodeId
          : null;
        set({
          timeline: result.data,
          receivedAt: Date.now(),
          loading: false,
          selectedNodeId
        });
        return result.data;
      } catch (error) {
        if (request !== requestVersion) return null;
        // A transient error retains this order's history, but never enables stale actions.
        const denied = isCreatorTimelineAccessError(error);
        set({
          loading: false,
          error:
            error instanceof Error
              ? error
              : new Error('Unable to load delivery timeline.'),
          ...(denied ? { timeline: null, writesDisabled: true } : {})
        });
        return null;
      }
    },
    disableWrites: () => set({ writesDisabled: true }),
    reset: () => {
      requestVersion += 1;
      set({
        orderId: '',
        timeline: null,
        receivedAt: 0,
        loading: false,
        error: null,
        writesDisabled: false,
        selectedNodeId: null
      });
    }
  })
);
